import { supabase } from '@/integrations/supabase/client';

interface CreateActivityParams {
  groupId: string;
  tipo: 'badge' | 'streak' | 'meta_concluida' | 'checkin' | 'habit_completed';
  payload: Record<string, any>;
  eventType: string;
}

export const gamificationService = {
  async createActivityWithPoints(params: CreateActivityParams) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      // 1. Verificar preferências do membro
      const shouldShare = await this.checkSharingPreferences(
        params.groupId, 
        user.id, 
        params.tipo
      );
      
      if (!shouldShare) return;
      
      // 2. Buscar pontos do evento
      const { data: pointsData } = await supabase
        .from('gamification_points')
        .select('points')
        .eq('event_type', params.eventType)
        .maybeSingle();
      
      const points = pointsData?.points || 0;
      
      // 3. Criar atividade
      const { error: activityError } = await supabase
        .from('group_activities')
        .insert({
          group_id: params.groupId,
          user_id: user.id,
          tipo: params.tipo,
          payload: params.payload
        });
      
      if (activityError) throw activityError;
      
      // 4. Atualizar contribution_score do membro
      const { data: memberData } = await supabase
        .from('group_members')
        .select('contribution_score')
        .eq('group_id', params.groupId)
        .eq('user_id', user.id)
        .single();

      if (memberData) {
        const { error: memberError } = await supabase
          .from('group_members')
          .update({ 
            contribution_score: (memberData.contribution_score || 0) + points
          })
          .eq('group_id', params.groupId)
          .eq('user_id', user.id);
        
        if (memberError) throw memberError;
      }
      
      // 5. Atualizar pontos_grupo
      const { data: groupData } = await supabase
        .from('groups')
        .select('pontos_grupo')
        .eq('id', params.groupId)
        .single();

      if (groupData) {
        const { error: groupError } = await supabase
          .from('groups')
          .update({ 
            pontos_grupo: (groupData.pontos_grupo || 0) + points
          })
          .eq('id', params.groupId);
        
        if (groupError) throw groupError;
      }
    } catch (error) {
      console.error('Error creating activity with points:', error);
    }
  },
  
  async checkSharingPreferences(
    groupId: string, 
    userId: string, 
    activityType: string
  ): Promise<boolean> {
    try {
      const { data } = await supabase
        .from('group_members')
        .select('share_badges, share_streaks, share_goals, share_checkins')
        .eq('group_id', groupId)
        .eq('user_id', userId)
        .maybeSingle();
      
      if (!data) return false;
      
      const preferenceMap: Record<string, keyof typeof data> = {
        'badge': 'share_badges',
        'streak': 'share_streaks',
        'meta_concluida': 'share_goals',
        'checkin': 'share_checkins',
        'habit_completed': 'share_checkins' // Hábitos compartilham mesma config de check-ins
      };
      
      const preferenceKey = preferenceMap[activityType];
      return preferenceKey ? data[preferenceKey] : true;
    } catch (error) {
      console.error('Error checking sharing preferences:', error);
      return false;
    }
  }
};
